import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logoSymbol from '../assets/logo-symbol.png';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../hooks/useBranding';
import PoweredByBlockBoard from './PoweredByBlockBoard';
import { 
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/solid';

const vendorMenuItems = [
  { name: 'Active Jobs', icon: ClipboardDocumentListIcon, section: 'jobs' },
  { name: 'Completed Work', icon: CheckCircleIcon, section: 'completed' },
  { name: 'My Services', icon: BuildingOfficeIcon, section: 'services' },
  { name: 'Payments', icon: CurrencyDollarIcon, section: 'payments' },
];

interface VendorLayoutProps {
  children: React.ReactNode;
  activeSection?: string;
  onSectionChange?: (section: string) => void;
}

const VendorLayout: React.FC<VendorLayoutProps> = ({ 
  children, 
  activeSection = 'jobs',
  onSectionChange 
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useBranding();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotifications = () => {
    // For now, show a simple notifications dialog
    alert('Notifications:\n\n• New work order assigned: Broken Playground Slide\n• Payment received: $250.00\n• System update: Vendor portal updated');
  };

  const handleSectionChange = (section: string) => {
    if (onSectionChange) {
      onSectionChange(section);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Top Header Bar */}
      <header className="glass-sidebar border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Company Info */}
            <div className="flex items-center space-x-4">
              {branding?.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={branding.name || 'Property logo'}
                  title={branding.name || ''}
                  className="h-12 w-auto max-w-[180px] object-contain"
                />
              ) : (
                <>
                  <img src={logoSymbol} alt="BlockBoard" className="w-10 h-10 object-contain" />
                  <div>
                    <h1 className="text-xl font-bold text-white">Vendor Portal</h1>
                    <p className="text-sm text-white/60">{user?.companyName || 'Contractor Dashboard'}</p>
                  </div>
                </>
              )}
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-2">
              {vendorMenuItems.map((item) => (
                <button
                  key={item.section}
                  onClick={() => handleSectionChange(item.section)}
                  className={`
                    ${activeSection === item.section 
                      ? 'nav-item-active' 
                      : 'nav-item'
                    }
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="ml-2">{item.name}</span>
                </button>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <button 
                onClick={handleNotifications}
                className="relative p-3 rounded-xl hover:bg-white/10 transition-colors group"
              >
                <BellIcon className="w-6 h-6 text-white/80 group-hover:text-white transition-colors" />
                <div className="absolute top-2 right-2 w-2 h-2 bg-[#22d3ee] rounded-full animate-pulse"></div>
              </button>

              {/* User Profile */}
              <div className="flex items-center space-x-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-white/60">{user?.serviceType || 'Contractor'}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {user?.name?.charAt(0).toUpperCase() || 'V'}
                  </span>
                </div>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="btn-secondary group"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5 group-hover:rotate-12 transition-transform duration-200" />
                <span className="hidden sm:inline ml-2">Logout</span>
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-xl hover:bg-white/10 transition-colors"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6 text-white" />
                ) : (
                  <Bars3Icon className="w-6 h-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-black/20 backdrop-blur-md border-t border-white/10">
            <div className="px-4 py-3 space-y-2">
              {vendorMenuItems.map((item) => (
                <button
                  key={item.section}
                  onClick={() => handleSectionChange(item.section)}
                  className={`
                    w-full ${activeSection === item.section 
                      ? 'nav-item-active' 
                      : 'nav-item'
                    }
                  `}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="ml-3">{item.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fade-in">
          <div className="floating-card">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center">
                <BuildingOfficeIcon className="w-6 h-6 text-[#22d3ee]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Welcome back, {user?.name}
                </h2>
                <p className="text-white/60">
                  Manage your work orders and track payments for {user?.companyName || 'your contracting business'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="glass-card min-h-[600px] animate-slide-up">
          {children}
        </div>
        <PoweredByBlockBoard />
      </main>

      {/* Footer */}
      <footer className="glass-sidebar border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <p className="text-white/60 text-sm">
              © 2024 BlockBoard Vendor Portal. Professional contractor management.
            </p>
            <div className="flex space-x-4 mt-4 sm:mt-0">
              <button className="text-white/60 hover:text-white text-sm transition-colors">
                Support
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="text-white/60 hover:text-white text-sm transition-colors"
              >
                Terms
              </button>
              <button
                onClick={() => navigate('/privacy')}
                className="text-white/60 hover:text-white text-sm transition-colors"
              >
                Privacy
              </button>
              <button
                onClick={() => navigate('/security')}
                className="text-white/60 hover:text-white text-sm transition-colors"
              >
                Security
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VendorLayout; 